import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import { StyledComponent } from '@emotion/styled';
import { FormattedMessage, MessageDescriptor } from 'react-intl-next';
import { useSelector } from 'react-redux';

import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { Popup } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';

import { getCurrentUserRole } from 'src/components/conversation/src/utils/get-current-user-role';
import { toFabricUser } from 'src/components/conversation-provider/utils';
import {
  getCurrentPullRequest,
  getIsStickyHeaderActive,
  getNavResolvedComments,
  getNavUnresolvedComments,
} from 'src/redux/pull-request/selectors';
import { getCurrentRepositoryUuid } from 'src/selectors/repository-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { getWorkspacePlan } from 'src/selectors/workspace-selectors';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { buttonAppearance, buttonSpacing } from '../constants';

import { CommentPreview } from './comment-preview';
import messages from './diff-comment-navigator.i18n';
import * as styles from './styles';
import { NavComment } from './to-nav-comment';

type CommentDropdownProps = {
  title: MessageDescriptor;
  emptyStateMessage: MessageDescriptor;
  expanded: boolean;
  comments: NavComment[];
  HeaderStyle: StyledComponent<any, any, any>;
  toggleDropdown: () => void;
  closeCommentNav: () => void;
  publishUiEventForCommentSelection: (commentId: number) => void;
};

function NoCommentsMessage(props: { message: MessageDescriptor }) {
  return (
    <styles.EmptyCommentSection>
      <InfoIcon
        label="No comments"
        primaryColor={token('color.icon.discovery')}
      />
      <FormattedMessage {...props.message} />
    </styles.EmptyCommentSection>
  );
}

export function CommentDropdown(props: CommentDropdownProps): JSX.Element {
  const {
    title,
    emptyStateMessage,
    expanded,
    toggleDropdown,
    HeaderStyle,
    comments,
    closeCommentNav,
    publishUiEventForCommentSelection,
  } = props;

  const Header = () => (
    <HeaderStyle onClick={toggleDropdown}>
      <FormattedMessage {...title} values={{ numThreads: comments.length }} />
      <styles.CollapseExpandButton>
        {expanded ? (
          <ChevronDownIcon label="collapse" testId="" />
        ) : (
          <ChevronUpIcon label="expand" />
        )}
      </styles.CollapseExpandButton>
    </HeaderStyle>
  );

  const commentPreviews = comments.map(comment => (
    <CommentPreview
      key={comment.id}
      comment={comment}
      closeCommentNav={closeCommentNav}
      publishUiEventForCommentSelection={publishUiEventForCommentSelection}
    />
  ));

  return (
    <>
      <Header />
      {expanded &&
        (comments.length ? (
          commentPreviews
        ) : (
          <NoCommentsMessage message={emptyStateMessage} />
        ))}
    </>
  );
}

type DiffCommentNavProps = {
  setIsCommentNavOpen: Dispatch<SetStateAction<boolean>>;
  isCommentNavOpen: boolean;
  isStickyHeader?: boolean;
};

type ExpandedDropdown = 'unresolved' | 'resolved';

export function DiffCommentNavigator(props: DiffCommentNavProps): JSX.Element {
  const {
    setIsCommentNavOpen,
    isCommentNavOpen,
    isStickyHeader = false,
  } = props;

  const [expandedDropdown, setExpandedDropdown] =
    useState<ExpandedDropdown>('unresolved');

  // If this is not being rendered in the sticky header,
  // we need to close the selector when the sticky header is activated.
  // This prevents the scrolling bug found in COREX-8483.
  const isStickyHeaderActive = useSelector(getIsStickyHeaderActive);
  useEffect(() => {
    if (isStickyHeaderActive && !isStickyHeader) {
      setIsCommentNavOpen(false);
    }
  }, [isStickyHeaderActive, isStickyHeader, setIsCommentNavOpen]);

  const { isWorkspacePaid } = useSelector(getWorkspacePlan);
  const user = useSelector(getCurrentUser);
  const pullRequest = useSelector(getCurrentPullRequest);
  const repoId = useSelector(getCurrentRepositoryUuid) || undefined;

  const publishUiEventForDiffCommentNav = (
    action: string,
    subject: string,
    extraAttributes?: object
  ) => {
    publishUiEvent({
      action,
      actionSubject: 'commentNav',
      actionSubjectId: subject,
      source: 'prView',
      attributes: {
        prStatus: pullRequest?.state,
        prId: pullRequest?.id,
        paid: isWorkspacePaid,
        repoId,
        role: getCurrentUserRole(toFabricUser(user), pullRequest),
        ...extraAttributes,
      },
    });
  };

  const unresolvedComments = useSelector(getNavUnresolvedComments);
  const resolvedComments = useSelector(getNavResolvedComments);
  const tabIndex = isStickyHeader ? -1 : 0;

  const toggleDropdown = () => {
    const expand =
      expandedDropdown === 'unresolved' ? 'resolved' : 'unresolved';
    setExpandedDropdown(expand);
    publishUiEventForDiffCommentNav('expand', `${expand}_comments`);
  };

  const setIsCommentNavOpenWithUiEvent = (open: boolean) => {
    setIsCommentNavOpen(open);
    const action = open ? 'opened' : 'closed';
    publishUiEventForDiffCommentNav(action, 'dropdown');
  };

  const content = () => {
    if (!unresolvedComments.length && !resolvedComments.length) {
      return (
        <styles.ExpandedPopup>
          <NoCommentsMessage message={messages.allCommentsEmpty} />
        </styles.ExpandedPopup>
      );
    }

    return (
      <styles.ExpandedPopup>
        <CommentDropdown
          title={messages.unresolvedCommentsTitle}
          emptyStateMessage={messages.unresolvedCommentsEmpty}
          expanded={expandedDropdown === 'unresolved'}
          comments={unresolvedComments}
          HeaderStyle={styles.TopHeaderSection}
          toggleDropdown={toggleDropdown}
          closeCommentNav={() => setIsCommentNavOpen(false)}
          publishUiEventForCommentSelection={(commentId: number) =>
            publishUiEventForDiffCommentNav('clicked', 'comment', {
              resolved: false,
              commentId,
            })
          }
        />
        <CommentDropdown
          title={messages.resolvedCommentsTitle}
          emptyStateMessage={messages.resolvedCommentsEmpty}
          expanded={expandedDropdown === 'resolved'}
          comments={resolvedComments}
          HeaderStyle={styles.BottomHeaderSection}
          toggleDropdown={toggleDropdown}
          closeCommentNav={() => setIsCommentNavOpen(false)}
          publishUiEventForCommentSelection={(commentId: number) =>
            publishUiEventForDiffCommentNav('clicked', 'comment', {
              resolved: true,
              commentId,
            })
          }
        />
      </styles.ExpandedPopup>
    );
  };

  return (
    <Popup
      id="comment-navigation-button"
      isOpen={isCommentNavOpen}
      onClose={() => setIsCommentNavOpenWithUiEvent(false)}
      placement="bottom-start"
      content={content}
      shouldFlip={false}
      trigger={triggerProps => (
        <Button
          {...triggerProps}
          appearance={buttonAppearance(isStickyHeader)}
          spacing={buttonSpacing(isStickyHeader)}
          onClick={() => setIsCommentNavOpenWithUiEvent(!isCommentNavOpen)}
          iconAfter={<ChevronDownIcon label="toggle-comment-popup" />}
          isSelected={isCommentNavOpen}
          tabIndex={tabIndex}
        >
          <FormattedMessage
            {...messages.dropdownTitle}
            values={{ numUnresolved: unresolvedComments.length }}
          />
        </Button>
      )}
    />
  );
}
